/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Card, Col, Dropdown, Image, Row, Tag } from "antd";

import { SelectedClientType } from "../../ducks/types";
import { PopUp } from "../../../../atoms/PopUp";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../../../config/hooks";
import { DeleteIcon, GreenCheckIcon } from "../../../../atoms/CustomIcons";
import ConfirmDelete from "../../../../atoms/ConfirmDelete";
import { getClientContacts, getPropertyFormData } from "../../ducks/actions";
import AddClientPopUp from "./PopUp";
import { PhoneInput } from "react-international-phone";
import { SelectOptionWithButton } from "../../../../atoms/FormFields/SelectOptionWithButton";
import { deleteClient } from "../../../Clients/ducks/services";
import SuccessMessage from "../../../../atoms/Toasts/SuccessMessage";
import { AxiosError } from "axios";
import ErrorMessage from "../../../../atoms/Toasts/ErrorMessage";
import { UseFormSetValue } from "react-hook-form";
import { MoreOutlined } from "@ant-design/icons";

interface PropsType {
  propertyClientData: SelectedClientType[];
  control: any;
  companyId: string;
  selectedClient: SelectedClientType | null;
  setSelectedClient: React.Dispatch<
    React.SetStateAction<SelectedClientType | null>
  >;
  setClientId: React.Dispatch<React.SetStateAction<string | null>>;
  setValue: UseFormSetValue<FormValues>;
}

interface combineDropDownType {
  label: string;
  options: { value: string; label: string; color?: string; type: string }[];
}

interface FormValues {
  name: string;
  address: string;
  select_client: string;
  category_ids: string[];
  address_note: string;
  client_contact_id: string[];
  responsible_for_account: string;
}

export default function PropertyClient(props: PropsType) {
  const {
    propertyClientData,
    control,
    setClientId,
    selectedClient,
    setSelectedClient,
    companyId,
    setValue,
  } = props;
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newClientPopUp, setNewClient] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [combineDropDown, setCombineDropDown] = useState<combineDropDownType[]>(
    []
  );

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            setNewClient(false);
            setModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
        >
          Edit details
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="delete-hover"
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
          icon={<DeleteIcon />}
        >
          Delete
        </Button>
      ),
    },
  ];

  const selectClientChange = (value: string | number | readonly string[]) => {
    const filteredClient: SelectedClientType | null =
      propertyClientData?.find(
        (client: { value: string }) => client?.value === value
      ) ?? null;
    setSelectedClient(filteredClient);
    setClientId(value as string);
    dispatch(getClientContacts(value as string));
  };

  const deleteFunction = () => {
    setLoading(true);
    deleteClient(selectedClient?.type ? [selectedClient?.value || ""] : [])
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        dispatch(getPropertyFormData(companyId));
        setSelectedClient(null);
        setValue("select_client", "");
        SuccessMessage("Client deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmDeletePopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete {selectedClient?.label}? <br />
            This action cannot be undone.
          </>
        }
        btnText="Delete"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const addNewClient = {
    visibility: modalOpen,
    class: "",
    content: (
      <AddClientPopUp
        onClose={() => setModalOpen(false)}
        selectedClient={selectedClient}
        newClientPopUp={newClientPopUp}
      />
    ),
    width: 541,
    onCancel: () => {
      setModalOpen(false);
    },
  };

  useEffect(() => {
    if (propertyClientData) {
      const individualOptions = Array.isArray(propertyClientData)
        ? propertyClientData
            ?.filter((i: { type: string }) => i?.type === "individual")
            ?.map((item: { label: string; value: string }) => ({
              label: item.label, // Material labels are normal strings
              value: item.value,
              type: "individual", // You can add this back if needed for other logic
            }))
        : [];

      // 2. Format the category options, rendering labels as Tags
      const companyOptions = Array.isArray(propertyClientData)
        ? propertyClientData
            ?.filter((i: { type: string }) => i?.type === "company")
            ?.map((item: { label: string; value: string }) => ({
              label: item.label, // Material labels are normal strings
              value: item.value,
              type: "company", // You can add this back if needed for other logic
            }))
        : [];

      // 3. Create the grouped structure
      const groupedSelectOptions = [
        {
          label: "Companies", // The header for the categories group
          options: companyOptions, // The array of category options (with Tags)
        },
        {
          label: "Individuals", // The header for the materials group
          options: individualOptions, // The array of material options
        },
      ];

      setCombineDropDown(groupedSelectOptions);
    }
  }, [propertyClientData, setCombineDropDown]);

  return (
    <>
      <Row gutter={20}>
        <Col span={24} style={{ marginBottom: "16px" }}>
          <h4 style={{ marginBottom: "4px" }}> Select client </h4>
          <p>The client is the property owning company or individual.</p>
        </Col>
        <Col span={24}>
          <SelectOptionWithButton
            fieldName="select_client"
            control={control}
            selectOption={combineDropDown}
            iProps={{
              placeholder: "Select existing or create a new one",
            }}
            addItemText="Add new client"
            onSelectChange={selectClientChange}
            onAddItemClick={() => {
              setNewClient(true);
              setModalOpen(true);
            }}
            className="bottom_0"
          />
        </Col>
        {selectedClient && (
          <Col span={24}>
            <Card style={{ marginBottom: "0px", marginTop: "8px" }}>
              <div
                className="titleBar"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "21px",
                }}
              >
                <div>
                  <span
                    className="userName"
                    style={{
                      fontSize: "16px",
                      lineHeight: "24px",
                      fontWeight: "700",
                      color: "#151718",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {selectedClient?.label}
                    <Tag
                      className="default_tag"
                      style={{
                        marginLeft: "8px",
                        textTransform: "capitalize",
                      }}
                    >
                      {selectedClient?.type}
                    </Tag>
                  </span>
                </div>
                <div className="actionBtns">
                  <Dropdown
                    menu={{ items }}
                    trigger={["click"]}
                    placement="bottomRight"
                    className="actionButton"
                  >
                    <Button
                      onClick={(x) => {
                        x.stopPropagation();
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      style={{
                        padding: "0px",
                        height: "auto",
                        border: "unset",
                        backgroundColor: "transparent",
                      }}
                      ref={dropdownRef}
                      className="only_icon"
                      icon={<MoreOutlined />}
                    />
                  </Dropdown>
                </div>
              </div>

              <div style={{ display: "flex", gap: "30px" }}>
                {selectedClient?.type === "individual" && (
                  <div>
                    <div
                      className="contactData"
                      style={{ display: "flex", gap: "8px" }}
                    >
                      <span
                        className="labelText"
                        style={{ color: "#6C7478", minWidth: "50px" }}
                      >
                        Phone:
                      </span>
                      <span className="ValueText" style={{ display: "flex" }}>
                        <PhoneInput disabled value={selectedClient?.phone} />
                        {selectedClient?.preferred_method_of_contact ===
                          "phone" && (
                          <GreenCheckIcon
                            style={{
                              marginLeft: "4px",
                            }}
                          />
                        )}
                      </span>
                    </div>
                    <div
                      className="contactData"
                      style={{ display: "flex", gap: "8px" }}
                    >
                      <span
                        className="labelText"
                        style={{ color: "#6C7478", minWidth: "50px" }}
                      >
                        Email:
                      </span>
                      <span className="ValueText">
                        {selectedClient?.email}
                        {selectedClient?.preferred_method_of_contact ===
                          "email" && (
                          <GreenCheckIcon
                            style={{
                              marginLeft: "4px",
                            }}
                          />
                        )}
                      </span>
                    </div>
                  </div>
                )}

                <div
                  className="contactData"
                  style={{ display: "flex", gap: "8px" }}
                >
                  <div
                    className="labelText"
                    style={{ color: "#6C7478", minWidth: "50px" }}
                  >
                    Address:
                  </div>
                  <div className="ValueText">{selectedClient?.address}</div>
                </div>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      <PopUp {...addNewClient} />
      <PopUp {...confirmDeletePopUp} />
    </>
  );
}
